import React, { useRef, useState } from "react";
import { api, formatError } from "../../api";
import { Upload, Loader2, X as IconX, FileSpreadsheet, Download, CheckCircle2, AlertCircle } from "lucide-react";

const CSV_HEADERS = ["name", "email", "phone", "current_role", "current_company", "source"];
const CSV_SAMPLE = `name,email,phone,current_role,current_company,source
Priya Sharma,priya.sharma@example.com,+91-9876543210,Senior SDE,Acme Corp,linkedin
Rahul Verma,rahul.v@example.com,+91-9123456780,Data Engineer,DataCo,referral
,no.name@example.com,,QA Lead,QualityWorks,manual
`;

/**
 * Bulk CSV import of sourced candidates onto a single posting.
 *
 * Flow:
 *   1) Pick or drag-drop a .csv (max 2 MB, max 500 rows).
 *   2) Submit → backend returns {added, skipped, errors} summary.
 *   3) Show result. On success, parent refreshes the applicants list.
 */
export default function BulkCsvImportModal({ posting, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const pid = posting?.id;

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      setErr("Only .csv files are accepted.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setErr("CSV is over 2 MB. Trim it down or split into batches.");
      return;
    }
    setErr("");
    setFile(f);
    setResult(null);
  };

  const downloadSample = () => {
    const blob = new Blob([CSV_SAMPLE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "skillconnect_candidates_template.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setErr("Pick a CSV first.");
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Do NOT set Content-Type manually — axios/browser must add the
      // multipart boundary parameter for FastAPI to parse the upload.
      const r = await api.post(`/postings/${pid}/applicants/bulk-csv`, fd);
      setResult(r.data);
    } catch (e2) {
      setErr(formatError(e2) || "Could not import CSV.");
    } finally { setBusy(false); }
  };

  const close = () => {
    if (result?.summary?.added > 0) onDone?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="bulk-csv-modal"
    >
      <div className="bg-white rounded-md w-full max-w-2xl shadow-card max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-rule">
          <div>
            <div className="eyebrow flex items-center gap-2">
              <FileSpreadsheet size={12} /> Bulk import
            </div>
            <h3 className="font-display font-bold text-xl mt-0.5 truncate">
              Import candidates from CSV
            </h3>
            <p className="text-xs text-muted mt-0.5 truncate">
              For role: {posting?.title}
            </p>
          </div>
          <button className="btn-ghost" onClick={close} aria-label="Close"
                  data-testid="bulk-csv-close"><IconX size={14}/></button>
        </div>

        {/* Body */}
        {!result ? (
          <form onSubmit={submit} className="p-5 space-y-4">
            <div className="rounded-sm border border-rule bg-paper/50 p-3 text-xs text-muted">
              <div className="font-medium text-default mb-1">CSV format</div>
              <div>Required column: <code className="font-mono text-default">email</code></div>
              <div>Optional columns: <code className="font-mono text-default">{CSV_HEADERS.filter(h => h !== "email").join(", ")}</code></div>
              <div className="mt-1">Max <b>500 rows</b>, <b>2 MB</b>. Existing candidates are reused by email.</div>
              <button
                type="button"
                className="btn-ghost mt-2"
                onClick={downloadSample}
                data-testid="bulk-csv-download-sample"
              >
                <Download size={12}/> Download template
              </button>
            </div>

            <div>
              <label className="eyebrow block mb-1.5">CSV file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={onPick}
                className="hidden"
                data-testid="bulk-csv-file-input"
              />
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => fileRef.current?.click()}
                  data-testid="bulk-csv-pick-file"
                >
                  <Upload size={12}/> {file ? "Replace file" : "Choose CSV"}
                </button>
                {file && (
                  <span className="text-sm text-muted truncate max-w-[260px]">
                    {file.name} · {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </div>
            </div>

            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm"
                   data-testid="bulk-csv-error">{err}</div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-rule">
              <button type="button" className="btn-ghost" onClick={close}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={busy || !file}
                      data-testid="bulk-csv-submit">
                {busy
                  ? <><Loader2 size={12} className="animate-spin"/> Importing…</>
                  : <><FileSpreadsheet size={12}/> Import candidates</>}
              </button>
            </div>
          </form>
        ) : (
          <ResultSummary result={result} onClose={close} />
        )}
      </div>
    </div>
  );
}

function ResultSummary({ result, onClose }) {
  const s = result?.summary || {};
  return (
    <div className="p-5 space-y-4" data-testid="bulk-csv-result">
      <div className="grid sm:grid-cols-3 gap-3">
        <KpiCell label="Added" value={s.added || 0} tone="ok" testid="bulk-csv-added" />
        <KpiCell label="Skipped" value={s.skipped || 0} tone="warn" testid="bulk-csv-skipped" />
        <KpiCell label="Errors" value={s.errors || 0} tone={s.errors ? "err" : "neutral"} testid="bulk-csv-errors" />
      </div>

      {result.errors?.length > 0 && (
        <div className="border border-rule rounded-sm">
          <div className="px-3 py-2 bg-red-50 border-b border-red-200 text-sm font-medium text-red-700 inline-flex items-center gap-1.5">
            <AlertCircle size={12}/> {result.errors.length} row(s) had errors
          </div>
          <div className="max-h-48 overflow-y-auto text-xs">
            {result.errors.slice(0, 50).map((e, i) => (
              <div key={i} className="px-3 py-1.5 border-b border-rule last:border-0"
                   data-testid={`bulk-csv-err-row-${i}`}>
                Row {e.row}{e.email ? ` (${e.email})` : ""}: {e.error}
              </div>
            ))}
            {result.errors.length > 50 && (
              <div className="px-3 py-1.5 text-muted italic">…and {result.errors.length - 50} more</div>
            )}
          </div>
        </div>
      )}

      {result.skipped?.length > 0 && (
        <div className="text-xs text-muted">
          Skipped {result.skipped.length} row(s) already attached to this posting.
        </div>
      )}

      <div className="flex items-center justify-end pt-2 border-t border-rule">
        <button className="btn-primary" onClick={onClose} data-testid="bulk-csv-done">
          <CheckCircle2 size={12}/> Done
        </button>
      </div>
    </div>
  );
}

function KpiCell({ label, value, tone, testid }) {
  const toneCls = {
    ok:      "bg-emerald-50 border-emerald-200 text-emerald-700",
    warn:    "bg-amber-50 border-amber-200 text-amber-700",
    err:     "bg-red-50 border-red-200 text-red-700",
    neutral: "bg-paper border-rule text-muted",
  }[tone || "neutral"];
  return (
    <div className={`rounded-sm border px-3 py-2 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide font-mono opacity-80">{label}</div>
      <div className="font-display font-bold text-2xl mt-0.5">{value}</div>
    </div>
  );
}
